import React, { useEffect, useRef, useState } from 'react';
import StepLayout from '../components/StepLayout';
import ResponsivePaysMore from '../components/ResponsivePaysMore';
import ApproveOnPhone from '../components/ApproveOnPhone';
import { OperatorReviewScreen } from '../components/StateScreens';
import { isTerminalErrorStatus } from '../utils/recoveryState';
import { isUKSeller } from '../utils/sellerRegion';
import { api } from '../api';
import { connectSSE } from '../sse';
import { claimConversion } from '../utils/conversionOnce';

// B-FE-APP-APPROVAL-3 (2026-05-17): the operator's Test 1 retry surfaced
// that V3 emits `needs_app_approval` during the email-change phase
// while the seller is on Step22Confirmation (the "Completing the secure
// transfer" gate that polls for emailChangeComplete). PR #357 fixed
// Step18 but the seller had already advanced through Steps 9-17 +
// Step18 by the time V3 hit the second app-approval — Step22 was the
// active surface and rendered the generic Finalising spinner.
// Same fix as PR #357/#358: subscribe to sse:takeover and swap to the
// ApproveOnPhone card on needs_app_approval. Kill-switch is the same
// REACT_APP_DISABLE_APP_APPROVAL_OVERLAY env var.
const APPROVAL_OVERLAY_DISABLED =
  process.env.REACT_APP_DISABLE_APP_APPROVAL_OVERLAY === '1';

// B-APPROVAL-RESOLVED (2026-05-17): clear overlay on the new resolved
// events V3 publishes between consecutive challenges. Setting this to
// '1' falls back to overlay-stays-until-terminal behavior.
const RESOLVED_EVENTS_DISABLED =
  process.env.REACT_APP_DISABLE_RESOLVED_EVENTS === '1';

// iter-26: rebuild per Thank You / Final Confirmation Page UI Spec.
// iter-38: gate the "You're all set" reveal on emailChangeComplete=true.
//
// The wizard lets the seller submit Payment Details, then renders this
// page. If V3's post-EPC cleanup is still in flight (verify-link click,
// make-primary, remove-old-primary), we MUST hold the seller here until
// the backend signals emailChangeComplete=true. Otherwise the seller
// could close the tab thinking they're done while the takeover is
// half-finished.
//
// While in the gate-pending state we render a Finalising spinner with
// the same copy as Step18 Finalizing. useInjection at the App level
// keeps watching `awaiting_email_change_2fa` so any LATE EPC challenge
// (e.g. LinkedIn re-prompting mid-cleanup) still pulls the seller to
// Step17 from this spinner.

// Become-a-referrer sign-up (the referral-app, NOT the ?ref= share link which
// lands on this wizard). Env-overridable; the fallback is the live deployed
// REFERRAL_PORTAL_BASE_URL. `?src=takeover-complete` lets the sign-up form tag
// where the applicant came from (prefills the "who referred you" field).
const REFERRAL_SIGNUP_URL = `${
  process.env.REACT_APP_REFERRAL_SIGNUP_URL || 'https://referrlinkedin.com'
}?src=takeover-complete`;

// Polling cadence + safety timeout. 3s mirrors useInjection. After 3
// minutes we show a "this is taking longer than expected" message but
// keep polling so the seller eventually unblocks once cleanup finishes.
const POLL_MS = 3000;
const SLOW_NOTICE_MS = 3 * 60 * 1000;

// B-FINALISING-WATCHDOG (2026-06-21): the email-change leg (step 9) can die
// agent-side (make-primary loop → max_iterations, the 18-min Celery hard kill,
// or a "Try again" re-dispatch hitting B-NO-POOL-PICK) while the BE status
// lingers at in_progress. Pre-fix this captive poll only ever exited on
// status==='completed', so a dead leg left the seller on the "Finalising"
// spinner indefinitely (observed live: stuck 20-78 min). Two backstops below
// guarantee the seller always reaches a terminal screen:
//   1. terminal-status detection — /status reports a terminal/failed status.
//   2. a hard elapsed watchdog — even if the status never flips off
//      in_progress, leave the spinner after WATCHDOG_MS. Sized ABOVE the 18-min
//      (1080s) Celery hard kill so a legitimately-slow-but-alive run still
//      completes normally before the watchdog can fire.
const WATCHDOG_MS = 20 * 60 * 1000;

export default function Step22Confirmation({ sessionId, sessionData }) {
  const [ready, setReady] = useState(false);
  const [slow, setSlow] = useState(false);
  // B-FINALISING-WATCHDOG (2026-06-21): null while pending; set to
  // { message } once a terminal status or the elapsed watchdog fires.
  const [terminal, setTerminal] = useState(null);
  // B-FE-APP-APPROVAL-3: render the polished card when V3 emits
  // needs_app_approval mid-email-change instead of the generic
  // "Completing the secure transfer" spinner.
  const [showApproveOverlay, setShowApproveOverlay] = useState(false);
  const [approveError, setApproveError] = useState('');
  const mountedAtRef = useRef(Date.now());

  // SSE subscription for app-approval events fired during email-change.
  useEffect(() => {
    if (APPROVAL_OVERLAY_DISABLED) return undefined;
    if (!sessionId) return undefined;
    const sse = connectSSE(sessionId, (event) => {
      if (!event || typeof event.type !== 'string') return;
      if (event.type === 'needs_app_approval') {
        // eslint-disable-next-line no-console
        console.log(
          '[B-FE-APPROVAL-3] showing ApproveOnPhone overlay, phase=email_change_step22',
          { sessionId, eventType: event.type },
        );
        setShowApproveOverlay(true);
        setApproveError('');
      } else if (
        !RESOLVED_EVENTS_DISABLED
        && (event.type === 'app_approval_resolved'
            || event.type === '2fa_resolved')
      ) {
        // eslint-disable-next-line no-console
        console.log(
          '[B-APPROVAL-RESOLVED] clearing overlay, phase=email_change_step22',
          { sessionId, eventType: event.type },
        );
        setShowApproveOverlay(false);
      } else if (
        event.type === 'email_change_success'
        || event.type === 'sync_complete'
        || event.type === 'login_success'
      ) {
        setShowApproveOverlay(false);
      } else if (
        typeof event.reason === 'string'
        && event.reason.startsWith('app_approval_timeout')
      ) {
        setApproveError(
          "We didn't receive an approval from your LinkedIn app. "
          + "Please open your LinkedIn mobile app to receive the next push notification.",
        );
      }
    });
    return () => {
      try { sse && sse.close && sse.close(); } catch (_) { /* noop */ }
    };
  }, [sessionId]);

  // Subtitle amount: £20 base + £5 ONLY once the badge is verified.
  // B-IDBADGE-HOLD-PENDING (2026-05-29): we don't promise the seller £25
  // on the strength of a claim — LinkedIn's verified badge is added via an
  // async phone/Persona review that can't be confirmed in-session, so the
  // confirmed amount reflects verified_badge_present. The operator credits
  // the +£5 out-of-band once the badge actually appears.
  const isUK = isUKSeller(sessionData);
  const symbol = isUK ? '£' : '$';
  const idBadgeVerified = sessionData?.verifiedBadgePresent === true;
  const idBadgeClaimed = sessionData?.idBadgeAccepted === true
    || sessionData?.idBadgeUpsell === 'Yes'
    || !!sessionData?.idBadgeAddedConfirmedAt;
  // +£5 only when the seller pursued the upsell AND the badge is verified
  // (pre-existing badges stay £20 — they never went through the upsell).
  const totalAmount = `${symbol}${idBadgeClaimed && idBadgeVerified ? 25 : 20}`;
  // Spec note: confirm with product whether referral bonus is fixed
  // £10 or country-conditional. Defaulting to country-conditional
  // (£10 UK, $10 US) which matches the form's general localization.
  const referralAmount = `${symbol}10`;

  useEffect(() => {
    if (!sessionId) {
      // Defensive: no sessionId means we hit the page outside a
      // takeover flow (dev jump, manual nav). Reveal immediately.
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      if (cancelled) return;
      try {
        const body = await api.getStatus(sessionId);
        if (cancelled) return;
        // B-CREDS-FE-EXPLICIT R4 (2026-05-29): strict gate. The BE only
        // writes status='completed' in vantage_sync_service.py AFTER:
        //   1. email_change_verified_at is set (G7 invariant), AND
        //   2. the accounts row is inserted, AND
        //   3. session-cleanup ran cleanly.
        // Previously this branch flipped ready=true on emailChangeComplete
        // alone (i.e. just step 1 of the 3). That left a window where the
        // success page revealed before vantage_sync finished — or worse,
        // never finished — making the wizard look complete when no
        // accounts row existed. Marcella's session 35edac24 (2026-05-28)
        // had emailChangeComplete=false the whole time (V3 never got
        // past step 4 login) but proved how easily the gate can be
        // out-of-sync with the BE's authoritative completion signal.
        //
        // Status === 'completed' is the only signal the BE writes when
        // the takeover is fully done end-to-end. We additionally require
        // emailChangeComplete=true as belt-and-suspenders so a future BE
        // path that flips status='completed' without going through
        // vantage_sync (shouldn't happen, but) cannot bypass this gate.
        if (body?.status === 'completed' && body?.emailChangeComplete === true) {
          setReady(true);
          return;
        }
        // B-FINALISING-WATCHDOG (2026-06-21): backstop #1 — the BE reported a
        // terminal/failed status (the email-change leg died, or an operator /
        // janitor flipped the session to operator_review). Stop the spinner and
        // show the honest "our team is reviewing" terminal instead of polling
        // forever. The App-level /state envelope normally renders
        // OperatorReviewScreen for these, but it polls a different endpoint and
        // can lag while the status is stale-in_progress; this is the
        // captive-surface backstop so the seller is never trapped here.
        if (body?.status && isTerminalErrorStatus(body.status)) {
          setTerminal({
            message: body.status_message_safe_for_seller
              || body.statusMessageSafeForSeller
              || null,
          });
          return; // stop polling — terminal reached
        }
      } catch (_) { /* transient — keep polling */ }
      const elapsed = Date.now() - mountedAtRef.current;
      if (elapsed >= SLOW_NOTICE_MS && !slow) setSlow(true);
      // B-FINALISING-WATCHDOG (2026-06-21): backstop #2 — hard elapsed cap.
      // Even if the status never flips off in_progress (e.g. the BE was
      // hard-killed mid-flight and nothing re-stamped the row), never leave the
      // seller on the spinner past WATCHDOG_MS.
      if (elapsed >= WATCHDOG_MS) {
        setTerminal({ message: null });
        return; // stop polling — watchdog fired
      }
      timer = setTimeout(poll, POLL_MS);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, slow]);

  // Meta Pixel conversion event — fires once when the gate opens.
  // Pushes the URL to /completed (replaceState — no full reload, React
  // state preserved) so the operator + GA pick up the path change, and
  // explicitly fires fbq('track', 'CompleteRegistration') because Meta
  // Pixel doesn't auto-track SPA route changes. Defensive: if fbq is
  // missing (ad blocker, Pixel not loaded yet), the page still renders
  // normally — we just don't fire the conversion. Guarded by a ref so
  // it only fires once even if React re-renders the component, and by
  // claimConversion so a reload of /completed (same session, persisted
  // storage) does not count the sign-up again; no sessionId, no conversion.
  const pixelFiredRef = useRef(false);
  useEffect(() => {
    if (!ready) return;
    if (pixelFiredRef.current) return;
    pixelFiredRef.current = true;
    try {
      if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
        const target = '/completed';
        if (window.location.pathname !== target) {
          // Preserve any query string (lead_id, etc) so attribution
          // tools that read it can still correlate.
          const next = target + window.location.search + window.location.hash;
          window.history.replaceState({ takeoverCompleted: true }, '', next);
        }
      }
      if (!claimConversion('CompleteRegistration', sessionId)) return;
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        // Standard Meta event for onboarding completion. Custom data
        // helps the operator slice attribution in Ads Manager. The
        // eventID lets a future server-side (CAPI) send dedupe against it.
        window.fbq('track', 'CompleteRegistration', {
          content_name: 'takeover_completed',
          status: 'applied',
        }, { eventID: `complete-registration-${sessionId}` });
      }
      // TikTok Pixel: mirror the Meta CompleteRegistration with TikTok's
      // standard `CompleteRegistration` event so the takeover-completed
      // conversion lands in TikTok Events Manager too. `window.ttq` is the
      // TikTok analytics object, so guard on `ttq.track`. The pixelFiredRef
      // and claimConversion guards above make this fire once per session.
      if (typeof window !== 'undefined' && window.ttq && typeof window.ttq.track === 'function') {
        window.ttq.track('CompleteRegistration', {
          content_name: 'takeover_completed',
          status: 'applied',
        });
      }
      // Snapchat Pixel: Snap's standard `SIGN_UP` on the completed takeover,
      // the same moment as Meta's CompleteRegistration (moved here from the
      // Step05 credentials submit, Filip 2026-10-27). No user fields: we send
      // no email, hashed or otherwise. `client_dedup_id` mirrors Meta's
      // eventID. `window.snaptr` only exists on a funnel built with a Snap
      // pixel id, so this is a no-op on every other funnel sharing this build.
      if (typeof window !== 'undefined' && typeof window.snaptr === 'function') {
        window.snaptr('track', 'SIGN_UP', {
          client_dedup_id: `complete-registration-${sessionId}`,
        });
      }
    } catch {
      // Never let analytics break the seller's view of the success page.
    }
  }, [ready, sessionId]);

  // B-FE-APP-APPROVAL-3: when V3 is polling for the seller's phone tap
  // during email-change, swap to the polished card before the
  // Finalising spinner. ready=true is the post-completion confirmation
  // page; this branch only fires during the pre-complete gate window.
  if (!ready && showApproveOverlay) {
    return (
      <StepLayout title="Approve sign-in on your phone" variant="centered" hideTrustpilot>
        <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.5, marginBottom: 20, textAlign: 'center' }}>
          LinkedIn needs you to confirm the email change.
        </p>
        <ApproveOnPhone
          subtitle="We'll continue automatically once LinkedIn confirms your approval."
          error={approveError}
        />
      </StepLayout>
    );
  }

  // B-FINALISING-WATCHDOG (2026-06-21): the email-change leg failed or the
  // elapsed watchdog fired — show the honest terminal screen instead of an
  // endless spinner. Mirrors the App-level envelope's OperatorReviewScreen so
  // the seller sees one consistent "we'll follow up" outcome. No CTA: this is a
  // late-stage failure the operator must reconcile (the buyer email may already
  // be live on the seller's account), not a clean restart.
  if (!ready && terminal) {
    return (
      <OperatorReviewScreen
        message={terminal.message
          || "Our team is reviewing your application. We'll follow up shortly. You can safely close this window."}
      />
    );
  }

  if (!ready) {
    return (
      <StepLayout title={null} hideTrustpilot>
        <div className="finalising" role="status" aria-live="polite">
          <h1 className="finalising-heading">Finalising your application</h1>
          <p className="finalising-sub">
            {slow
              ? 'Almost there, finishing up a couple more steps on LinkedIn.'
              : 'This is the final step where we check everything on your LinkedIn account. It can take a few minutes. Please keep this window open.'}
          </p>
          <div className="finalising-spinner" aria-hidden="true" />
          <div className="finalising-status">
            <span className="finalising-status__text">
              {slow ? 'Verifying with LinkedIn' : 'Completing the secure transfer'}
            </span>
          </div>
          <div className="finalising-warn">
            <span className="finalising-warn__icon" aria-hidden="true">&#9432;</span>
            <span>Please don&rsquo;t close this window</span>
          </div>
        </div>
      </StepLayout>
    );
  }

  return (
    <StepLayout title={null} hideTrustpilot>
      <div className="confirmation">
        <div
          className="confirmation-block"
          role="status"
          aria-live="polite"
        >
          <div className="confirmation-check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path
                d="M5 12 L10 17 L19 7"
                fill="none"
                stroke="#16A34A"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h1 className="confirmation-heading">You&rsquo;re all set</h1>

          <p className="confirmation-sub">
            Once our team verifies your details, your{' '}
            <strong className="confirmation-money">{totalAmount}</strong>{' '}
            will land in your bank within 24 hours.
          </p>
        </div>

        <ResponsivePaysMore placement="confirmation" sessionData={sessionData} />

        {/* Refer-a-friend: the referral flow is now live. Invite finished
            sellers to become referrers and earn per successful referral. */}
        <div className="refer-card">
          <span className="refer-pill">
            <span className="refer-pill__icon" aria-hidden="true">&#128176;</span>
            Earn more
          </span>
          <div className="refer-amount">{referralAmount}</div>
          <div className="refer-supporting">
            <span>for every successful referral</span>
            <span className="refer-sep" aria-hidden="true">&middot;</span>
            <span className="refer-unlimited">
              <span className="refer-unlimited__icon" aria-hidden="true">&#8734;</span>
              uncapped, refer as many friends as you like
            </span>
          </div>
          <p className="refer-fineprint">
            You earn {referralAmount} every time someone you refer completes their
            setup, with no limit on how much you can earn.
          </p>
          <a
            className="primary-cta refer-cta"
            href={REFERRAL_SIGNUP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Become a referrer
          </a>
        </div>
      </div>
    </StepLayout>
  );
}
